// Reads what a .docx really looks like when opened in Word: style inheritance (basedOn chain, document defaults),
// theme fonts, direct paragraph/run formatting, every section's margins, page size and page-number fields.
// Each measure is weighted by how many characters of body text it covers, so one differently formatted
// heading doesn't fail the paper and a whole body pasted in Calibri does.

export type Style = "APA" | "MLA" | "Chicago";

const W = "http://schemas.openxmlformats.org/wordprocessingml/2006/main";

interface ParaProps { line?: number; lineRule?: string; before?: number; after?: number; firstLine?: number; hanging?: number }
interface RunProps { font?: string; fontTheme?: string; size?: number }

function attr(el: Element | undefined | null, name: string): string | null {
  if (!el) return null;
  return el.getAttributeNS(W, name) ?? el.getAttribute("w:" + name);
}
function child(el: Element | null | undefined, name: string): Element | undefined {
  if (!el) return undefined;
  return Array.from(el.children).find((c) => c.localName === name);
}
function num(v: string | null): number | undefined {
  if (v === null || v === "") return undefined;
  const n = Number(v);
  return Number.isFinite(n) ? n : undefined;
}

function readPara(pPr: Element | undefined): ParaProps {
  const out: ParaProps = {};
  if (!pPr) return out;
  const sp = child(pPr, "spacing");
  if (sp) {
    const line = num(attr(sp, "line"));
    if (line !== undefined) { out.line = line; out.lineRule = attr(sp, "lineRule") || "auto"; }
    const b = num(attr(sp, "before")); if (b !== undefined) out.before = b;
    const a = num(attr(sp, "after")); if (a !== undefined) out.after = a;
    if (attr(sp, "beforeAutospacing") === "1" || attr(sp, "beforeAutospacing") === "true") out.before = 280;
    if (attr(sp, "afterAutospacing") === "1" || attr(sp, "afterAutospacing") === "true") out.after = 280;
  }
  const ind = child(pPr, "ind");
  if (ind) {
    const f = num(attr(ind, "firstLine")); if (f !== undefined) { out.firstLine = f; out.hanging = 0; }
    const h = num(attr(ind, "hanging")); if (h !== undefined) { out.hanging = h; out.firstLine = 0; }
    const fc = num(attr(ind, "firstLineChars")); if (fc !== undefined && f === undefined) out.firstLine = fc * 2.4; // ~ 1 char = 1/100 em at 12 pt
  }
  return out;
}
function readRun(rPr: Element | undefined): RunProps {
  const out: RunProps = {};
  if (!rPr) return out;
  const rf = child(rPr, "rFonts");
  if (rf) {
    const theme = attr(rf, "asciiTheme");
    const ascii = attr(rf, "ascii") || attr(rf, "hAnsi");
    if (theme) { out.fontTheme = theme; out.font = undefined; }
    else if (ascii) { out.font = ascii; out.fontTheme = undefined; }
  }
  const sz = num(attr(child(rPr, "sz"), "val"));
  if (sz !== undefined) out.size = sz / 2;
  return out;
}
const merge = <T extends object>(base: T, over: T): T => {
  const o = { ...base } as Record<string, unknown>;
  for (const [k, v] of Object.entries(over)) if (v !== undefined) o[k] = v;
  // A direct font choice replaces a theme font and vice versa.
  const ov = over as RunProps;
  if (ov.font !== undefined) o.fontTheme = undefined;
  if (ov.fontTheme !== undefined) o.font = undefined;
  return o as T;
};

interface StyleDef { id: string; type: string; basedOn?: string; name: string; isDefault: boolean; p: ParaProps; r: RunProps }

export interface DocParts { document: string; styles?: string; theme?: string; headers?: string[]; parse?: (xml: string) => Document }

export interface Check { id: string; label: string; pass: boolean | null; detail: string; info?: boolean }
export interface FormatReport {
  checks: Check[];
  bodyChars: number;
  bodyParagraphs: number;
  fonts: { name: string; share: number }[];
  sizes: { name: string; share: number }[];
}

const REF_HEADING = /^(references?|reference list|works cited|bibliography|sources)$/i;
const pct = (x: number) => `${Math.round(x * 100)}%`;
const inch = (twips: number) => twips / 1440;

export function analyzeDocx(parts: DocParts, style: Style): FormatReport {
  const parse = parts.parse || ((x: string) => new DOMParser().parseFromString(x, "application/xml"));
  const doc = parse(parts.document);

  // ---- styles
  const styles = new Map<string, StyleDef>();
  let docP: ParaProps = {};
  let docR: RunProps = {};
  if (parts.styles) {
    const sd = parse(parts.styles);
    const dd = sd.getElementsByTagNameNS(W, "docDefaults")[0];
    if (dd) {
      docR = readRun(child(child(dd, "rPrDefault") as Element, "rPr"));
      docP = readPara(child(child(dd, "pPrDefault") as Element, "pPr"));
    }
    for (const s of Array.from(sd.getElementsByTagNameNS(W, "style"))) {
      const id = attr(s, "styleId") || "";
      styles.set(id, {
        id, type: attr(s, "type") || "paragraph",
        basedOn: attr(child(s, "basedOn"), "val") || undefined,
        name: (attr(child(s, "name"), "val") || id).toLowerCase(),
        isDefault: attr(s, "default") === "1" || attr(s, "default") === "true",
        p: readPara(child(s, "pPr")), r: readRun(child(s, "rPr")),
      });
    }
  }
  const defaultParaStyle = Array.from(styles.values()).find((s) => s.type === "paragraph" && s.isDefault);

  let themeMinor = "Calibri", themeMajor = "Calibri Light";
  if (parts.theme) {
    const td = parse(parts.theme);
    const minor = td.getElementsByTagNameNS("*", "minorFont")[0]?.getElementsByTagNameNS("*", "latin")[0]?.getAttribute("typeface");
    const major = td.getElementsByTagNameNS("*", "majorFont")[0]?.getElementsByTagNameNS("*", "latin")[0]?.getAttribute("typeface");
    if (minor) themeMinor = minor;
    if (major) themeMajor = major;
  }
  const themeFont = (t: string) => (t.toLowerCase().startsWith("major") ? themeMajor : themeMinor);

  const chain = (id: string | undefined): StyleDef[] => {
    const out: StyleDef[] = [];
    const seen = new Set<string>();
    let cur = id ? styles.get(id) : undefined;
    while (cur && !seen.has(cur.id)) { seen.add(cur.id); out.unshift(cur); cur = cur.basedOn ? styles.get(cur.basedOn) : undefined; }
    return out;
  };

  // ---- paragraphs
  const fontChars = new Map<string, number>();
  const sizeChars = new Map<string, number>();
  let totalChars = 0;
  let lineDouble = 0, lineSingleish = 0, lineOther = 0, lineChars = 0;
  const lineSeen = new Map<string, number>();
  let bodyParas = 0, indented = 0, spaced = 0, hangingParas = 0;
  // Reference list (B-0149): entries after a References / Works Cited / Bibliography heading must hang 0.5".
  let inRefs = false, refHeadingFound = false, refEntries = 0, refHanging = 0;

  for (const p of Array.from(doc.getElementsByTagNameNS(W, "p"))) {
    let inTable = false;
    for (let a: Element | null = p.parentElement; a; a = a.parentElement) if (a.localName === "tbl" || a.localName === "txbxContent") { inTable = true; break; }
    if (inTable) continue;

    const pPr = child(p, "pPr");
    const styleId = attr(child(pPr, "pStyle"), "val") || defaultParaStyle?.id;
    const pChain = chain(styleId);
    let para = docP;
    let paraRun = docR;
    for (const s of pChain) { para = merge(para, s.p); paraRun = merge(paraRun, s.r); }
    para = merge(para, readPara(pPr));
    const styleName = pChain.length ? pChain[pChain.length - 1].name : "";
    const isHeading = /^(heading|title|subtitle|toc|caption)/.test(styleName);

    let paraChars = 0;
    let paraSize = 0, paraSizeLen = 0;
    for (const r of Array.from(p.getElementsByTagNameNS(W, "r"))) {
      let len = 0;
      for (const t of Array.from(r.children)) {
        if (t.localName === "t") len += (t.textContent || "").length;
        else if (t.localName === "tab") len += 1;
      }
      if (!len) continue;
      let run = paraRun;
      const rPr = child(r, "rPr");
      const rStyle = attr(child(rPr, "rStyle"), "val");
      if (rStyle) for (const s of chain(rStyle)) run = merge(run, s.r);
      run = merge(run, readRun(rPr));
      const fontName = run.fontTheme ? themeFont(run.fontTheme) : run.font || (parts.styles ? "Times New Roman" : "unknown");
      const size = run.size ?? 10;
      fontChars.set(fontName, (fontChars.get(fontName) || 0) + len);
      sizeChars.set(String(size), (sizeChars.get(String(size)) || 0) + len);
      totalChars += len;
      paraChars += len;
      if (len > paraSizeLen) { paraSizeLen = len; paraSize = size; }
    }
    if (!paraChars) continue;

    const ptext = Array.from(p.getElementsByTagNameNS(W, "t")).map((t) => t.textContent || "").join("").trim();
    if (ptext.length <= 40 && REF_HEADING.test(ptext)) { inRefs = true; refHeadingFound = true; }
    else if (inRefs && ptext.length <= 40 && /^appendix\b/i.test(ptext)) inRefs = false;
    const isRefEntry = inRefs && !REF_HEADING.test(ptext) && ptext.length >= 20;
    if (isRefEntry) { refEntries++; if ((para.hanging || 0) >= 576 && (para.hanging || 0) <= 864) refHanging++; }

    // Line spacing, weighted by characters
    const size = paraSize || 10;
    let kind: "double" | "single" | "other";
    let label: string;
    const line = para.line ?? 240;
    const rule = para.lineRule || "auto";
    if (rule === "auto") {
      const m = line / 240;
      label = `${Math.round(m * 100) / 100}×`;
      kind = m >= 1.9 && m <= 2.1 ? "double" : m <= 1.25 ? "single" : "other";
    } else {
      const pt = line / 20;
      label = `exactly ${pt} pt`;
      // "Exactly 24 pt" is double for 12 pt type; accept 1.9–2.1 × the font size.
      kind = pt >= size * 1.9 && pt <= size * 2.3 ? "double" : pt <= size * 1.25 ? "single" : "other";
    }
    if (kind === "double") lineDouble += paraChars; else if (kind === "single") lineSingleish += paraChars; else lineOther += paraChars;
    lineChars += paraChars;
    lineSeen.set(label, (lineSeen.get(label) || 0) + paraChars);

    if (paraChars >= 60 && !isHeading && !isRefEntry) {
      bodyParas++;
      if ((para.hanging || 0) > 0) hangingParas++;
      else if ((para.firstLine || 0) >= 600 && (para.firstLine || 0) <= 840) indented++;
      if ((para.before || 0) > 0 || (para.after || 0) > 0) spaced++;
    }
  }

  const shares = (m: Map<string, number>) =>
    Array.from(m.entries()).map(([name, n]) => ({ name, share: totalChars ? n / totalChars : 0 })).sort((a, b) => b.share - a.share);
  const fonts = shares(fontChars);
  const sizes = shares(sizeChars);
  const fmtMix = (l: { name: string; share: number }[], unit = "") => l.slice(0, 3).map((x) => `${x.name}${unit} (${pct(x.share)})`).join(", ");

  // ---- sections
  const sects = Array.from(doc.getElementsByTagNameNS(W, "sectPr"));
  const margins: { t: number; b: number; l: number; r: number }[] = [];
  const pageSizes = new Set<string>();
  for (const s of sects) {
    const m = child(s, "pgMar");
    if (m) margins.push({ t: num(attr(m, "top")) ?? NaN, b: num(attr(m, "bottom")) ?? NaN, l: num(attr(m, "left")) ?? NaN, r: num(attr(m, "right")) ?? NaN });
    const sz = child(s, "pgSz");
    if (sz) {
      const w = num(attr(sz, "w")) ?? 0, h = num(attr(sz, "h")) ?? 0;
      const [pw, ph] = attr(sz, "orient") === "landscape" ? [h, w] : [w, h];
      pageSizes.add(`${(pw / 1440).toFixed(2)}×${(ph / 1440).toFixed(2)}`);
    }
  }

  const checks: Check[] = [];

  // Margins
  if (margins.length) {
    const bad = margins.filter((m) => [m.t, m.b, m.l, m.r].some((v) => !(Math.abs(inch(v) - 1) < 0.05)));
    const first = margins[0];
    const found = `top ${inch(first.t).toFixed(2)}", bottom ${inch(first.b).toFixed(2)}", left ${inch(first.l).toFixed(2)}", right ${inch(first.r).toFixed(2)}"`;
    checks.push({
      id: "margins", label: 'Margins (1" on all sides)', pass: bad.length === 0,
      detail: bad.length === 0 ? `Found: ${found}${margins.length > 1 ? ` (all ${margins.length} sections)` : ""}` : `Found: ${found}${margins.length > 1 ? `; ${bad.length} of ${margins.length} sections differ` : ""}`,
    });
  } else checks.push({ id: "margins", label: 'Margins (1" on all sides)', pass: null, detail: "Couldn't detect margins in this document" });

  // Page size
  if (pageSizes.size) {
    const ok = Array.from(pageSizes).every((s) => s === "8.50×11.00");
    checks.push({ id: "page", label: 'Page size (8.5" × 11" Letter)', pass: ok, detail: `Found: ${Array.from(pageSizes).join(", ")}${ok ? "" : " — A4 and other sizes aren't what US style guides assume"}` });
  }

  // Font + size
  if (totalChars) {
    const topFont = fonts[0]?.name || "";
    const topSize = Number(sizes[0]?.name);
    const okShare = (names: (f: string) => boolean) => fonts.filter((f) => names(f.name)).reduce((a, f) => a + f.share, 0);
    const tnr = (f: string) => /times new roman/i.test(f);
    if (style === "APA") {
      // APA 7 accepts several fonts, each with its own size.
      const apa: Record<string, number> = { "times new roman": 12, calibri: 11, arial: 11, georgia: 11, "lucida sans unicode": 10, "computer modern": 10 };
      const key = Object.keys(apa).find((k) => topFont.toLowerCase().includes(k));
      const okFont = !!key;
      checks.push({
        id: "font", label: "Font (APA 7: Times New Roman 12, Calibri 11, Arial 11, Georgia 11, Lucida Sans Unicode 10)",
        pass: okFont && fonts[0].share >= 0.9,
        detail: `Found: ${fmtMix(fonts)}${okFont && fonts[0].share < 0.9 ? " — body text mixes fonts" : ""}`,
      });
      checks.push({
        id: "size", label: key ? `Font size (${apa[key]} pt for ${topFont})` : "Font size",
        pass: key ? Math.abs(topSize - apa[key]) < 0.5 && sizes[0].share >= 0.9 : false,
        detail: `Found: ${fmtMix(sizes, " pt")}`,
      });
    } else if (style === "MLA") {
      checks.push({ id: "font", label: "Font (MLA: any easily readable face; Times New Roman is the usual choice)", pass: fonts[0].share >= 0.9, detail: `Found: ${fmtMix(fonts)}${fonts[0].share < 0.9 ? " — body text mixes fonts" : ""}`, info: true });
      checks.push({ id: "size", label: "Font size (MLA: 12 pt)", pass: Math.abs(topSize - 12) < 0.5 && sizes[0].share >= 0.9, detail: `Found: ${fmtMix(sizes, " pt")}` });
    } else {
      checks.push({ id: "font", label: "Font (Chicago: Times New Roman recommended; other readable faces accepted)", pass: okShare(tnr) >= 0.9 ? true : fonts[0].share >= 0.9 ? null : false, detail: `Found: ${fmtMix(fonts)}${okShare(tnr) < 0.9 && fonts[0].share >= 0.9 ? " — acceptable if your instructor allows another readable face" : ""}` });
      checks.push({ id: "size", label: "Font size (Chicago: 12 pt; 11 pt is acceptable)", pass: topSize >= 11 && topSize <= 12 && sizes[0].share >= 0.9, detail: `Found: ${fmtMix(sizes, " pt")}` });
    }
  } else {
    checks.push({ id: "font", label: "Font", pass: null, detail: "No body text found in this document" });
    checks.push({ id: "size", label: "Font size", pass: null, detail: "No body text found in this document" });
  }

  // Line spacing
  if (lineChars) {
    const dShare = lineDouble / lineChars;
    const mix = Array.from(lineSeen.entries()).sort((a, b) => b[1] - a[1]).slice(0, 3).map(([k, v]) => `${k} (${pct(v / lineChars)})`).join(", ");
    checks.push({
      id: "spacing", label: "Line spacing (double-spaced throughout)", pass: dShare >= 0.9,
      detail: dShare >= 0.9 ? `Double-spaced for ${pct(dShare)} of the text` : `Found: ${mix}`,
    });
  } else checks.push({ id: "spacing", label: "Line spacing (double-spaced throughout)", pass: null, detail: "No body text found in this document" });

  // Paragraph spacing & indent
  if (bodyParas) {
    checks.push({
      id: "gap", label: "No extra space between paragraphs", pass: spaced / bodyParas < 0.1,
      detail: spaced ? `${spaced} of ${bodyParas} paragraphs have space before/after set — Word's default 8 pt after is the usual culprit` : "No extra space before or after body paragraphs",
    });
    const denom = bodyParas - hangingParas;
    if (denom > 0) checks.push({
      id: "indent", label: 'First line of each paragraph indented 0.5"', pass: indented / denom >= 0.8,
      detail: `${indented} of ${denom} body paragraphs have a 0.5" first-line indent${hangingParas ? ` (${hangingParas} hanging-indent entries such as references were skipped)` : ""}`,
    });
  }

  // Reference list hanging indent
  const refLabel = 'Reference list: hanging indent of 0.5" on each entry';
  if (refEntries) {
    checks.push({
      id: "refindent", label: refLabel, pass: refHanging / refEntries >= 0.8,
      detail: `${refHanging} of ${refEntries} reference entries have a 0.5" hanging indent${refHanging < refEntries ? ' — select the list, open Paragraph, and set Special to Hanging, 0.5"' : ""}`,
    });
  } else if (refHeadingFound) {
    checks.push({ id: "refindent", label: refLabel, pass: null, detail: "A reference-list heading was found but no entries followed it" });
  } else {
    checks.push({ id: "refindent", label: refLabel, pass: null, detail: "No References, Works Cited or Bibliography heading found, so the hanging indent wasn't checked" });
  }

  // Page numbers
  if (parts.headers) {
    const has = parts.headers.some((h) => /<w:instrText[^>]*>[^<]*\bPAGE\b|<w:fldSimple[^>]*\bPAGE\b/.test(h));
    checks.push({ id: "pageno", label: "Page numbers in the header (top right)", pass: has, detail: has ? "A page-number field was found in a header or footer" : "No page-number field found in any header or footer" });
  }

  return { checks, bodyChars: totalChars, bodyParagraphs: bodyParas, fonts, sizes };
}

/** Shown inline when a file cannot be read as a Word document, so a bad file is never a silent blank page. */
export const BAD_DOCX_MESSAGE = "This isn't a valid .docx file, so nothing could be checked. Open it in Word, Google Docs or LibreOffice and use Save As .docx (a renamed .doc or .pdf won't work), then drop it again.";
